import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLookups } from "@/context/LookupContext";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Trash2, Edit3, Clock, Package, Search } from "lucide-react";
import { norm } from "@/lib/normalize";
import { formatINR } from "@/lib/currency";
import { useConfirm } from "@/components/ConfirmDialog";
import {
  Scissors,
  Palette,
  Sparkles,
  Brush,
  Droplets,
  Flower2,
  Footprints,
  Hand,
  HandHeart,
  Eye,
  Heart,
  FlaskConical,
  Waves,
  Leaf,
  Bath
} from "lucide-react";

const iconMap = {
    SCISSORS: Scissors,
    SPARKLES: Sparkles,
    HAND: Hand,
    HANDHEART: HandHeart,
    FLOWER: Flower2,
    FLOWER2: Flower2,
    BRUSH: Brush,
    DROPLETS: Droplets,
    FOOTPRINTS: Footprints,
    CLOCK: Clock,
    PALETTE: Palette,
    EYE: Eye,
    HEART: Heart,
    FLASKCONICAL: FlaskConical,
    WAVES: Waves,
    LEAF: Leaf,
    BATH: Bath
};

const empty = { name: "", category: "Hair", duration_min: 60, price: 0, description: "" };
const emptyConsumable = { productId: "", quantity: 1, baseUnit: "" };

export default function Services() {
  const { user } = useAuth();
  const { lookups } = useLookups();
  const serviceCategoryList = lookups?.serviceCategories || [];
  const serviceCategories = serviceCategoryList.map((c) => c.name || c);
  const [rows, setRows] = useState([]);
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [consumables, setConsumables] = useState([]);
  const [editId, setEditId] = useState(null);
  const [filterCategory, setFilterCategory] = useState("");
  const [searchText, setSearchText] = useState("");
  const isAdmin = norm(user.roleName) === "admin" || norm(user.roleName) === "receptionist" || norm(user.roleName) === "manager";

  const load = async () => {
    try { const r = await api.get("/services"); setRows(r.data); }
    catch { toast.error("Failed"); }
  };

  const loadByCategory = async (categoryId) => {
    try { const r = await api.get(`/services/category/${categoryId}`); setRows(r.data); }
    catch { toast.error("Failed"); }
  };

  const reloadCurrentCategory = () => {
    if (filterCategory && filterCategory !== "all") {
      const catObj = serviceCategoryList.find((c) => String(c.id) === filterCategory);
      if (catObj) loadByCategory(catObj.id);
      else load();
    } else {
      load();
    }
  };

  const loadProducts = async () => {
    try { setProducts((await api.get("/products/consumable")).data); } catch { /* ignore */ }
  };

  // Default to first category on initial load
  useEffect(() => {
    loadProducts();
    load();
    if (!filterCategory) {
      setFilterCategory("all");
    }
  }, [serviceCategoryList.length]); // eslint-disable-line

  const onCategoryChange = (val) => {
    setFilterCategory(val);
    if (val && val !== "all") {
      const catObj = serviceCategoryList.find((c) => String(c.id) === val);
      if (catObj) loadByCategory(catObj.id);
    } else {
      load();
    }
  };

  const openNew = () => { setForm(empty); setConsumables([]); setEditId(null); setOpen(true); };
  const openEdit = (s) => {
    setForm({ name: s.name, category: s.categoryName || s.category || "", durationMinutes: s.durationMinutes, price: s.price, description: s.description || "" });
    setConsumables((s.products || []).map((c) => ({ productId: String(c.productId), quantity: c.quantityRequired, baseUnit: c.baseUnit || c.unit || "" })));
    setEditId(s.id);
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      const selectedCategory = serviceCategoryList.find((cat) => (cat.name || cat) === form.category);
      const servicePayload = {
        ...form,
        categoryId: selectedCategory?.id || null,
        categoryName: form.category,
        durationMinutes: Number(form.durationMinutes),
        price: Number(form.price),
      };
      let serviceId = editId;
      if (editId) {
        await api.put(`/services/${editId}`, servicePayload);
      } else {
        const res = await api.post("/services", servicePayload);
        serviceId = res.data?.id || res.data;
      }

      // Save service-product mappings
      const mappings = consumables.filter((c) => c.productId).map((c) => ({
        serviceId: Number(serviceId),
        productId: Number(c.productId),
        quantityRequired: Number(c.quantity),
        unit: c.baseUnit,
      }));
      if (mappings.length > 0) {
        await api.post("/service-products", mappings);
      }

      toast.success("Saved");
      setOpen(false); reloadCurrentCategory();
    } catch { toast.error("Failed"); }
  };

  const confirm = useConfirm();
  const del = async (id) => {
    if (!(await confirm("Delete this service?"))) return;
    try { await api.delete(`/services/${id}`); toast.success("Deleted"); reloadCurrentCategory(); }
    catch { toast.error("Failed"); }
  };

  return (
    <div className="space-y-8" data-testid="services-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="overline mb-2">Catalog</div>
          <h1 className="font-heading text-4xl text-white inline-flex items-center gap-3"><Scissors className="w-8 h-8 text-cyan-500" /> Services & Pricing</h1>
        </div>
        {isAdmin && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <button data-testid="add-service-btn" onClick={openNew} className="gold-button px-5 py-2.5 rounded-full inline-flex items-center gap-2"><Plus className="w-4 h-4" /> Add Service</button>
            </DialogTrigger>
            <DialogContent className="bg-emerald-surface border-gold-subtle text-white max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-heading text-2xl">{editId ? "Edit" : "New"} service</DialogTitle></DialogHeader>
              <form onSubmit={save} className="space-y-4">
                <div><Label>Name</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-emerald-elev border-gold-subtle mt-1" data-testid="svc-name-input" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Category</Label>
                    <Select value={form.category} onValueChange={(val) => setForm({ ...form, category: val })}>
                      <SelectTrigger className="mt-1 bg-emerald-elev border-gold-subtle text-white h-9">
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        {serviceCategories.map((c) => (
                          <SelectItem key={c} value={c}>{c}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>Duration (min)</Label><Input required type="number" value={form.durationMinutes} onChange={(e) => setForm({ ...form, durationMinutes: e.target.value })} className="bg-emerald-elev border-gold-subtle mt-1" /></div>
                </div>
                <div><Label>Price (₹)</Label><Input required type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="bg-emerald-elev border-gold-subtle mt-1" data-testid="svc-price-input" /></div>
                <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-emerald-elev border-gold-subtle mt-1" /></div>

                {/* Products Used (Consumables) */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-sm font-medium inline-flex items-center gap-1"><Package className="w-3.5 h-3.5 text-gold" /> Products Used</Label>
                    <button type="button" onClick={() => setConsumables([...consumables, { ...emptyConsumable }])} className="text-xs text-gold hover:underline inline-flex items-center gap-1"><Plus className="w-3 h-3" /> Add</button>
                  </div>
                  {consumables.length > 0 && (
                    <div className="border border-gold-subtle rounded-lg overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow className="border-gold-subtle hover:bg-transparent">
                            <TableHead className="th-gold text-xs">Product</TableHead>
                            <TableHead className="th-gold text-xs w-24">Qty Used</TableHead>
                            <TableHead className="th-gold text-xs w-24">Base Unit</TableHead>
                            <TableHead className="th-gold w-10"></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {consumables.map((c, idx) => (
                            <TableRow key={idx} className="border-gold-subtle">
                              <TableCell className="p-1">
                                <Select value={c.productId} onValueChange={(val) => {
                                  const p = products.find((pr) => String(pr.id) === val);
                                  const updated = [...consumables];
                                  updated[idx] = { ...updated[idx], productId: val, baseUnit: p.baseUnit || p?.unit || "" };
                                  setConsumables(updated);
                                }}>
                                  <SelectTrigger className="bg-emerald-elev border-gold-subtle text-white h-8 text-xs">
                                    <SelectValue placeholder="Select product" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {products.map((p) => (
                                      <SelectItem key={p.id} value={String(p.id)}>{p.name}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </TableCell>
                              <TableCell className="p-1">
                                <Input type="number" min="1" value={c.quantity} onChange={(e) => {
                                  const updated = [...consumables];
                                  updated[idx] = { ...updated[idx], quantity: e.target.value };
                                  setConsumables(updated);
                                }} className="bg-emerald-elev border-gold-subtle text-white h-8 text-xs" />
                              </TableCell>
                              <TableCell className="p-1">
                                <Input value={c.baseUnit} disabled className="bg-emerald-elev border-gold-subtle text-emerald-muted h-8 text-xs" />
                              </TableCell>
                              <TableCell className="p-1">
                                <button type="button" onClick={() => setConsumables(consumables.filter((_, i) => i !== idx))} className="text-emerald-muted hover:text-red-300"><Trash2 className="w-3.5 h-3.5" /></button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                  {consumables.length === 0 && <p className="text-xs text-emerald-muted">No consumable products mapped yet.</p>}
                </div>

                <button type="submit" className="gold-button w-full h-11 rounded-md" data-testid="save-service-btn">Save</button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-emerald-muted" />
          <Input
            placeholder="Search by name"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            className="pl-9 bg-emerald-surface border-gold-subtle text-white h-10"
            data-testid="services-search"
          />
        </div>
        <Select value={filterCategory} onValueChange={onCategoryChange}>
          <SelectTrigger className="bg-emerald-surface border-gold-subtle text-white h-10">
            <SelectValue placeholder="All Categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {serviceCategoryList.map((c) => (
              <SelectItem key={c.id || c.name} value={String(c.id || c.name)}>{c.name || c}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {rows.filter((s) => {
          const search = searchText.toLowerCase();
          if (search && !(s.name || "").toLowerCase().includes(search)) return false;
          return true;
        }).map((s) => {
          const categoryObj = serviceCategoryList.find((cat) => (cat.name || cat) === s.category);
          const iconKey = (categoryObj?.categoryIcon || s.categoryIcon || "").toUpperCase();
          const IconComponent = iconMap[iconKey] || Scissors;
          return (
          <div key={s.id} className="bg-emerald-surface border border-gold-subtle rounded-2xl p-6 flex flex-col" data-testid={`service-card-${s.id}`}>
            <div className="overline">{s.category}</div>
            <div className="flex items-center justify-between mt-2">
              <h3 className="font-heading text-2xl text-white">{s.name}</h3>
              <IconComponent className="w-5 h-5 text-gold" />
            </div>
            <p className="text-sm text-emerald-muted mt-2 flex-1">{s.description}</p>
            <div className="mt-6 flex items-center justify-between">
              <span className="text-gold text-xl">{formatINR(s.price)}</span>
              <span className="text-xs text-emerald-muted inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {s.durationMinutes} min</span>
            </div>
            {s.products && s.products.length > 0 && (
              <div className="mt-4 pt-4 border-t border-gold-subtle">
                <div className="text-xs text-emerald-muted uppercase tracking-widest mb-2 inline-flex items-center gap-1"><Package className="w-3 h-3 text-gold" /> Products Used</div>
                <div className="space-y-1">
                  {s.products.map((c, i) => (
                    <div key={i} className="flex justify-between text-xs">
                      <span className="text-white">{c.productName || "—"}</span>
                      <span className="text-emerald-muted">{c.quantityRequired} {c.unit}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {isAdmin && (
              <div className="mt-4 pt-4 border-t border-gold-subtle flex gap-2">
                <button onClick={() => openEdit(s)} className="text-xs text-emerald-muted hover:text-gold inline-flex items-center gap-1" data-testid={`edit-service-${s.id}`}><Edit3 className="w-3 h-3" /> Edit</button>
                <button onClick={() => del(s.id)} className="text-xs text-emerald-muted hover:text-red-300 inline-flex items-center gap-1 ml-auto" data-testid={`delete-service-${s.id}`}><Trash2 className="w-3 h-3" /> Delete</button>
              </div>
            )}
          </div>
        );})}
      </div>
    </div>
  );
}
